import React, { useState, useMemo, useEffect } from "react";
import { withTranslation } from "react-i18next";
import { Button, Grid, TextField, useMediaQuery } from "@mui/material";
import PropTypes from "prop-types";
import moment from "moment";

import SelectField from "Components/Common/SelectField";
import BreadCrumb from "Components/Common/BreadCrumb";
import { dateFormats } from "common/constant";
import {
  getOptionLabel,
  reportOptions,
  getOptionValue,
  reportTypeEnum,
} from "common/enums";
import BaseTable from "Components/Common/BaseTable";
import Backdrop from "Components/Common/Backdrop";
import DatePicker from "Components/Common/DatePicker";
import ActiveDisconnectCategoryOrTariffTable from "./ActiveDisconnectCategoryOrTariffTable";
import ComplaintTable from "./ComplaintTable";
import GERCTable from "./GERCTable";
import ConsumerAndMeteringStatusTable from "./ConsumerAndMeteringStatusTable";
import MonthlyApplicationCountTable from "./MonthlyApplicationCountTable";
import MaximumDemandConsumerWiseTable from "./MaximumDemandConsumerWiseTable";
import PowerAbstractTable from "pages/Admin/PowerAbstract/PowerAbstractTable";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import { convertCamelTextToCapitalizeText } from "utils/convertCamelTextToCapitalizeText";
import { dateFormatter } from "utils/dateFormatter";

import { useLazyGetBillingCycleOptionsQuery } from "apis/BillingCycle";
import { useLazyDownloadExcelQuery, useLazyGetQuery } from "slices/api";

const BY_DEFAULT_VALUE = 1;
const RECORDS_PER_PAGE = 20;

const applyColumnPropsOnKey = "connectionAddress";

const consumerMasterDataColumns = [
  "virtualId",
  "consumerNumber",
  "consumerName",
  "connectionAddress",
  "locationOfService",
  "tariff",
  "category",
  "supplyVoltage",
  "typeOfMetering",
  "contractDemand",
  "unit",
  "meterInstallationDate",
  "meterSerialNumber",
  "mf",
  "electricityDuty",
  "applicationsStatus",
];

const consumerC4DataColumns = [
  "virtualId",
  "block",
  "building",
  "category",
  "tariff",
  "connectionAddress",
  "consumerName",
  "consumerNumber",
  "unit",
  "dateOfApplication",
  "dateOfConnection",
  "dateOfDisconnection",
];

const consumerVirtualAccountColumns = [
  { title: "Company LU CD1", field: "companyLUCD1" },
  { title: "APAC", field: "apac" },
  { title: "Dealer CD", field: "dealerCD" },
  { title: "Dealer Name", field: "dealerName" },
  { title: "Dr Narration", field: "drNarration" },
  { title: "Cr Narration", field: "crNarration" },
  { title: "Email Id", field: "emailId" },
  { title: "Mobile", field: "mobile" },
  { title: "Other Info1", field: "otherInfo1" },
  { title: "Other Info2", field: "otherInfo2" },
  { title: "Remarks", field: "remarks" },
  { title: "Map Virtual APAC", field: "mapVirtualAPAC" },
  { title: "Remitter APAC Check Required", field: "remitterAPACCheckRequired" },
  { title: "Partial Check Flag", field: "partialCheckFlag" },
  { title: "IMPS Enabled Flag", field: "impsEnabledFlag" },
];

const getTableColumns = (
  columns,
  columnData = {},
  fieldKey,
  valueKey,
  parentKey = ""
) => {
  Object.keys(columnData).forEach((key) => {
    if (columnData[key] && typeof columnData[key] === "object") {
      getTableColumns(
        columns,
        columnData[key],
        fieldKey,
        valueKey,
        `${parentKey ? `${parentKey}.` : ""}${key}`
      );
    } else {
      columns.push({
        [fieldKey]: convertCamelTextToCapitalizeText(key),
        [valueKey]: `${parentKey ? `${parentKey}.` : ""}${key}`,
        columnProps:
          applyColumnPropsOnKey === key ? { style: { minWidth: "350px" } } : {},
      });
    }
  });
};

const Reports = ({ t }) => {
  const [selectedReportId, setSelectedReportId] = useState(BY_DEFAULT_VALUE);
  const [rowTableData, setRowTableData] = useState([]);
  const [billCycleId, setBillCycleId] = useState("");
  const [query, setQuery] = useState({
    year: moment(),
    dateTime: moment(),
    reportType: getOptionValue("reportTypeEnum", "New Connection"),
  });
  const sm = useMediaQuery("(max-width: 599px)");

  const [
    downloadExcel,
    { isLoading: isExcelFileDownloading, isFetching: isDownloadExcelFetching },
  ] = useLazyDownloadExcelQuery();

  const [
    getTableData,
    { data: { data: tableData } = {}, isLoading, isFetching },
  ] = useLazyGetQuery();
  const [
    fetchBillingOptions,
    {
      data: { data: billingCycleOptions } = {},
      isLoading: isBillingCycleOptionsLoading,
    },
  ] = useLazyGetBillingCycleOptionsQuery();

  const reportsData = useMemo(
    () => [
      {
        value: 1,
        endpoint: "/report/application-status-history",
        downloadSheetEndpoint: "/report/all-application-status-history-excel",
        sheetName: "Application Status History-All",
      },
      {
        value: 2,
        endpoint: "/power/application/report/list-of-application",
        downloadSheetEndpoint:
          "/power/application/report/list-of-application-excel",
        sheetName: "List of Applications",
      },
      {
        value: 3,
        endpoint: "/power/application/report/monthly-application-count",
        downloadSheetEndpoint:
          "/power/application/report/monthly-application-count-excel",
        Component: MonthlyApplicationCountTable,
        sheetName: "Monthly Application Count",
      },
      {
        value: 4,
        endpoint: "/report/consumer",
        downloadSheetEndpoint: "/report/consumer-master-data-excel",
        sheetName: "Consumer Master Data",
      },
      {
        value: 5,
        endpoint: "/report/consumer",
        downloadSheetEndpoint: "/report/consumer-data-c4-excel",
        sheetName: "Consumer Data-C4",
      },
      {
        value: 6,
        endpoint: "/report/consumer-activated-disconnected-tariff-wise",
        downloadSheetEndpoint:
          "/report/consumer-activated-disconnected-tariff-wise-excel",
        sheetName: "Active and Disconnected Consumers",
        isQueryRequired: true,
        Component: ActiveDisconnectCategoryOrTariffTable,
        componentProps: {
          keys: [
            "activatedAndDisconnectedMonthWise",
            "utilityConsumerTariffActivatedAndDisconnected",
            "tariffName",
            "activated",
            "disconnected",
          ],
        },
      },
      {
        value: 7,
        endpoint: "/report/consumer-activated-disconnected-category-wise",
        downloadSheetEndpoint:
          "/report/consumer-activated-disconnected-category-wise-excel",
        sheetName: "Active and Disconnected Category Wise",
        isQueryRequired: true,
        Component: ActiveDisconnectCategoryOrTariffTable,
        componentProps: {
          keys: [
            "activatedAndDisconnectedMonthWise",
            "utilityConsumerCategoryActivatedAndDisconnected",
            "category",
            "activated",
            "disconnected",
          ],
        },
      },
      {
        value: 8,
        endpoint: "/report/contact-detail-report",
        downloadSheetEndpoint: "/report/contact-details-excel",
        sheetName: "Contact Details",
      },
      {
        value: 9,
        endpoint: "/report/backup-power",
        downloadSheetEndpoint: "/report/backup-power-details-excel",
        sheetName: "Backup Power Details",
      },
      {
        value: 10,
        endpoint: "/report/net-metering",
        downloadSheetEndpoint: "/report/net-metering-excel",
        sheetName: "Net Metering",
      },
      {
        value: 11,
        endpoint: "/report/consumer-complaint",
        sheetName: "Number of Complaints",
        downloadSheetEndpoint: "/report/consumer-complaint-excel",
        isQueryRequired: true,
        Component: ComplaintTable,
      },
      {
        value: 12,
        endpoint: "/report/sop-gerc",
        sheetName: "GERC",
        isQueryRequired: true,
        downloadSheetEndpoint: "/report/sop-gerc-excel",
        Component: GERCTable,
      },
      {
        value: 13,
        endpoint: "/report/maximum-demand-consumer-wise",
        sheetName: "Maximum Demand Consumer Wise",
        isQueryRequired: true,
        downloadSheetEndpoint: "/report/maximum-demand-consumer-wise-excel",
        Component: MaximumDemandConsumerWiseTable,
      },
      // {
      //   value: 14,
      //   endpoint: "",
      //   sheetName: "Maximum Demand-TR Wise",
      //   isQueryRequired: true,
      // },
      {
        value: 15,
        endpoint: "/report/connection-load-category-wise",
        downloadSheetEndpoint: "/report/connection-load-category-wise-excel",
        sheetName: "Category Wise Connected Load",
      },
      {
        value: 16,
        endpoint: "/report/asset-detail",
        downloadSheetEndpoint: "/report/asset-detail-excel",
        sheetName: "Asset Details",
      },
      {
        value: 17,
        endpoint: "/report/metering-status",
        downloadSheetEndpoint: "/report/metering-status-excel",
        sheetName: "Metering Status",
        isQueryRequired: true,
        Component: ConsumerAndMeteringStatusTable,
        componentProps: {
          keys: [
            "meterTypeCountMonthWise",
            "meterTypeCount",
            "meterName",
            "count",
          ],
        },
      },
      {
        value: 18,
        endpoint: "/report/sim-detail",
        downloadSheetEndpoint: "/report/sim-detail-excel",
        sheetName: "SIM Details",
      },
      {
        value: 19,
        endpoint: "/report/consumer-category-wise",
        downloadSheetEndpoint: "/report/consumer-category-wise-excel",
        sheetName: "consumer Category Wise",
        isQueryRequired: true,
        Component: ConsumerAndMeteringStatusTable,
        componentProps: {
          keys: [
            "utilityConsumerMonthWise",
            "utilityConsumerCategoryCount",
            "category",
            "count",
          ],
        },
      },
      {
        value: 20,
        endpoint: "/report/consumer-tariff-wise",
        sheetName: "consumer Tariff Wise",
        downloadSheetEndpoint: "/report/consumer-tariff-wise-excel",
        isQueryRequired: true,
        Component: ConsumerAndMeteringStatusTable,
        componentProps: {
          keys: ["tariffRecords", "tariffDetails", "tariffName", "count"],
        },
      },
      {
        value: 21,
        endpoint: `/report/billing-cycle/${billCycleId}/bill-calculation`,
        sheetName: "Billing Calculation",
        downloadSheetEndpoint: `/report/billing-cycle/${billCycleId}/bill-calculation-excel`,
      },
      {
        value: 22,
        endpoint: "/report/dispatched-bill-report",
        isQueryDateTime: true,
        sheetName: "Dispatched Bills",
        downloadSheetEndpoint: "/report/dispatched-bill-report-excel",
      },
      {
        value: 23,
        endpoint: `/report/billing-cycle/${billCycleId}/electricity-bill-abstract-report`,
        sheetName: "Power Abstract",
        isServerSidePaginationRequired: true,
        downloadSheetEndpoint: `/report/billing-cycle/${billCycleId}/electricity-bill-abstract-report-excel`,
        Component: PowerAbstractTable,
        componentProps: {
          powerAbstractTableProps: { billingCycleId: billCycleId },
        },
      },
      {
        value: 24,
        endpoint: `/report/billing-cycle/${billCycleId}/invoice-uploader-report`,
        sheetName: "Invoice Uploader",
        downloadSheetEndpoint: `/report/billing-cycle/${billCycleId}/invoice-uploader-report-excel`,
      },
      {
        value: 25,
        endpoint: "/report/consumer-virtual-account-report",
        sheetName: getOptionLabel("reportTypeEnum", query.reportType),
        isQueryDateTime: true,
        downloadSheetEndpoint: "/report/consumer-virtual-account-report-excel",
      },
    ],
    [billCycleId, query.reportType]
  );

  const {
    endpoint,
    isQueryRequired,
    isQueryDateTime,
    Component,
    downloadSheetEndpoint,
    isServerSidePaginationRequired,
    sheetName,
    componentProps,
  } = useMemo(
    () => reportsData.find(({ value }) => value === selectedReportId),
    [selectedReportId, reportsData]
  );

  const isConsumerVirtualAccountReportSelected = useMemo(
    () =>
      getOptionLabel("reportOptions", selectedReportId) ===
      "Consumer Virtual Account",
    [selectedReportId]
  );

  const columns = useMemo(() => {
    let tableColumns = [];
    if (
      selectedReportId ===
      getOptionValue("reportOptions", "Consumer Master Data")
    ) {
      consumerMasterDataColumns.map((item) =>
        tableColumns.push({
          title: convertCamelTextToCapitalizeText(item),
          field: item,
          columnProps:
            applyColumnPropsOnKey === item
              ? { style: { minWidth: "350px" } }
              : {},
        })
      );
    } else if (
      selectedReportId === getOptionValue("reportOptions", "Consumer Data")
    ) {
      consumerC4DataColumns.map((item) =>
        tableColumns.push({
          title: convertCamelTextToCapitalizeText(item),
          field: item,
          columnProps:
            applyColumnPropsOnKey === item
              ? { style: { minWidth: "350px" } }
              : {},
        })
      );
    } else if (isConsumerVirtualAccountReportSelected) {
      tableColumns = consumerVirtualAccountColumns;
    } else {
      getTableColumns(tableColumns, tableData?.[0], "title", "field");
    }
    return tableColumns;
  }, [tableData, selectedReportId]);

  const getReportData = async () => {
    try {
      if (
        (getOptionValue("reportOptions", "Billing Calculation") !==
          selectedReportId &&
          getOptionValue("reportOptions", "Invoice Uploader") !==
            selectedReportId) ||
        ((getOptionValue("reportOptions", "Billing Calculation") ===
          selectedReportId ||
          getOptionValue("reportOptions", "Invoice Uploader") ===
            selectedReportId) &&
          billCycleId)
      ) {
        const { data: { data: tableData } = {} } = await getTableData(
          {
            endpoint,
            query: isQueryRequired
              ? { year: query.year.format("YYYY") }
              : isQueryDateTime
              ? {
                  dateTime: query.dateTime.format("YYYY-MM-DD"),
                  ...(isConsumerVirtualAccountReportSelected && {
                    reportType: query.reportType,
                  }),
                }
              : {},
          },
          true
        );
        if (tableData) {
          const data = Array.isArray(tableData) ? tableData : [tableData];

          const updatedTableData = data.map((row) =>
            Object.entries(row).reduce((res, [key, value]) => {
              if (
                typeof value === "string" &&
                moment(value, dateFormats, true).isValid()
              ) {
                return {
                  ...res,
                  [key]: convertUTCDateToLocalDate(value).format("DD-MM-YYYY"),
                };
              }
              return { ...res, [key]: value };
            }, {})
          );
          setRowTableData(updatedTableData || []);
        }
      }
    } catch (error) {}
  };

  const getReportUrl = async () => {
    if (downloadSheetEndpoint) {
      try {
        await downloadExcel({
          endpoint: downloadSheetEndpoint,
          sheetName,
          query: isQueryRequired
            ? { year: query.year.format("YYYY") }
            : isQueryDateTime
            ? {
                dateTime: query.dateTime.format("YYYY-MM-DD"),
                ...(isConsumerVirtualAccountReportSelected && {
                  reportType: query.reportType,
                }),
              }
            : {},
        });
      } catch (error) {}
    }
  };

  useEffect(() => {
    if (!isServerSidePaginationRequired) {
      getReportData();
    }
  }, [
    endpoint,
    query?.year,
    query?.dateTime,
    query.reportType,
    sheetName,
    isServerSidePaginationRequired,
  ]);

  const billCycleValue = useMemo(
    () => billingCycleOptions?.find(({ id }) => id === billCycleId),
    [billingCycleOptions, billCycleId]
  );

  useEffect(() => {
    (async () => {
      if (
        getOptionLabel("reportOptions", selectedReportId) ===
          "Billing Calculation" ||
        getOptionLabel("reportOptions", selectedReportId) ===
          "Power Abstract" ||
        getOptionLabel("reportOptions", selectedReportId) === "Invoice Uploader"
      ) {
        try {
          await fetchBillingOptions();
        } catch (error) {}
      }
    })();
  }, [selectedReportId]);

  useEffect(() => {
    if (billingCycleOptions?.length) {
      setBillCycleId(billingCycleOptions[0].id);
    }
  }, [billingCycleOptions]);

  return (
    <div className="h-100">
      <Backdrop
        open={
          isLoading ||
          isFetching ||
          isExcelFileDownloading ||
          isDownloadExcelFetching ||
          isBillingCycleOptionsLoading
        }
      />

      <BreadCrumb title={t("Reports")} pageTitle={t("Dashboard")} />

      <Grid container columnSpacing={2} rowSpacing={1} mt="7px" mb="7px">
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <SelectField
            onChange={(_, { value }) => {
              setSelectedReportId(value);
              setRowTableData([]);
            }}
            value={reportOptions.find(
              ({ value }) => value === selectedReportId
            )}
            options={reportOptions}
            renderInput={(params) => {
              delete params.inputProps.id;
              delete params.id;
              return <TextField {...params} label={t("Select Report Type")} />;
            }}
          />
        </Grid>
        {(getOptionLabel("reportOptions", selectedReportId) ===
          "Billing Calculation" ||
          getOptionLabel("reportOptions", selectedReportId) ===
            "Power Abstract" ||
          getOptionLabel("reportOptions", selectedReportId) ===
            "Invoice Uploader") &&
          billCycleValue && (
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <SelectField
                value={{
                  label: billCycleValue.billDateDuration,
                  value: billCycleValue.id,
                }}
                getOptionLabel={({ billDateDuration }) => billDateDuration}
                getOptionValue={({ id }) => id}
                onChange={(_, { value }) => setBillCycleId(value)}
                options={billingCycleOptions}
                renderInput={(params) => {
                  delete params.inputProps.id;
                  delete params.id;
                  return (
                    <TextField {...params} label={t("Select bill cycle")} />
                  );
                }}
              />
            </Grid>
          )}
        {isConsumerVirtualAccountReportSelected && (
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SelectField
              label={t("Report Type")}
              value={reportTypeEnum.find(
                ({ value }) => query.reportType === value
              )}
              onChange={(_, { value }) =>
                setQuery((prev) => ({
                  ...prev,
                  reportType: value,
                }))
              }
              options={reportTypeEnum}
            />
          </Grid>
        )}
        {isQueryRequired ? (
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <DatePicker
              slotProps={{
                textField: {
                  size: "small",
                },
              }}
              label={t("Select Year")}
              className="w-100"
              views={["year"]}
              format={dateFormatter(query.year)}
              value={query.year}
              disableFuture
              onChange={(date) => {
                setQuery((prevState) => ({
                  ...prevState,
                  year: date,
                }));
              }}
            />
          </Grid>
        ) : (
          isQueryDateTime && (
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <DatePicker
                slotProps={{
                  textField: {
                    size: "small",
                  },
                }}
                label={t("Select Month and Year")}
                className="w-100"
                views={["month", "year"]}
                value={query.dateTime}
                disableFuture
                onChange={(date) => {
                  setQuery((prev) => ({
                    ...prev,
                    dateTime: date,
                  }));
                }}
              />
            </Grid>
          )
        )}
        <Grid xs={12} sm={6} md={4} lg={3} sx={{ pt: 1.25, pl: 2 }}>
          <Button
            sx={{ maxHeight: "40px" }}
            variant="contained"
            color="primary"
            onClick={getReportUrl}
          >
            {t("Download")}
          </Button>
        </Grid>
      </Grid>

      {(!!rowTableData.length || isServerSidePaginationRequired) &&
      Component ? (
        <Component
          data={rowTableData}
          {...(componentProps || {})}
          maxHeight={sm ? "calc(100vh - 300px)" : "calc(100vh - 260px)"}
        />
      ) : (
        <BaseTable
          columns={columns}
          data={rowTableData}
          pageSize={RECORDS_PER_PAGE}
          maxHeight={sm ? "calc(100vh - 300px)" : "calc(100vh - 260px)"}
        />
      )}
    </div>
  );
};

Reports.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(Reports);
